/**
 * The canvas: seven columns, Monday to Sunday, as a FLEX ROW with elastic bases.
 *
 * The 07.09 programator handoff replaced v3's `repeat(7, minmax(0, 1fr))` with a flex row: a rest
 * day takes a fixed 84px and a training day a 232px basis it grows out of, up to 496px. So a week
 * carrying three workouts gives each of them room to be read rather than a seventh of the canvas
 * each. A grid track cannot express that — it has no way to take a different minimum depending on
 * what stands in it.
 *
 * **The week always fits.** The mock pins `flex-shrink: 0`, which makes seven trainings overflow;
 * `KANON - builder planow` says the opposite ("dni treningowe dzielą resztę po równo — tydzień
 * zawsze mieści się na ekranie") and the owner settled it that way on 7 September 2026. So with the
 * panel closed there is no horizontal scrollbar at any day count, and the sideways scroll exists
 * only while the day panel covers the right-hand end — see `.week[data-panel-room]`, which grows
 * that room OUT OF FLOW so not one column changes width when the panel opens.
 *
 * ── It is the phase tabs' panel ──────────────────────────────────────────────────────────────
 * `role="tabpanel"`, labelled by the selected tab, so a screen-reader user who arrows across the
 * phase band is told what they are now looking at. `tabIndex={0}` because the panel scrolls and a
 * scrollable region has to be reachable by keyboard.
 *
 * The panel also carries a visually hidden `h2`. Two reasons, and the first is the one that made it
 * mandatory: the document's only other headings here are the `h1` in the top bar and the `h3` on
 * every session card, so the outline jumped H1 → H3 and a reader navigating by heading was told the
 * cards belong to a section that does not exist. The second is that it gives the canvas a name in
 * the outline — "Wzorzec tygodnia" — which is what the tab band is switching between.
 *
 * The columns are a `list` rather than a `grid`. Real grid semantics — the ones the view ADR §12
 * asks for — belong to the cycle matrix, where cells have row and column coordinates a trainer
 * navigates between. Here each column is one item with one card in it; announcing it as a
 * seven-column grid with one row would describe a structure that is not there.
 *
 * ── The scroll fade, and why the pane is wrapped ─────────────────────────────────────────────
 * At 1440×900 a heavy week clips its cards mid-card, and the only cue was the scrollbar — invisible
 * on macOS until the trainer is already scrolling. `.canvas` exists to hold the fade: it is a
 * non-scrolling positioning context around the scroller, which is what lets the fade sit over the
 * clipped edge and cost no layout. A `position: sticky` pseudo-element inside the scroller was the
 * obvious alternative and is wrong here — a sticky flex item still occupies flow, so it would add
 * its own height to `scrollHeight` and manufacture 24px of scroll on a week that otherwise fits.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { useSwapReplay } from '~/shared/lib/useSwapReplay';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import swap from '~/shared/ui/swapReplay.module.css';
import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';
import { useAutoScroll } from '~/shared/ui/dnd/useAutoScroll';

import type { AddDayRefusalView } from '../model/builderChrome';
import type { OverlayRoom } from '../model/revealDay';
import type { WeekdayColumnView } from '../model/weekViewModel';

import { DayColumn } from './DayColumn';
import { RENAME_HINT_ID, type DayCardActions, type SessionMoveProps } from './SessionCard';
import styles from './WeekPane.module.css';

/** The id of the ONE keyboard instruction on the canvas — see `RENAME_HINT_ID` for the same reason. */
export const MOVE_HINT_ID = 'plan-builder-move-hint';

interface WeekPaneProps {
  readonly id: string;
  readonly columns: readonly WeekdayColumnView[];
  /** The id of the phase tab this panel belongs to. */
  readonly labelledBy: string;
  /** Forwarded to each column, unchanged — the canvas holds positions, not edits. */
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  /** Open the day dock. Forwarded unchanged, for the same reason. */
  readonly onProgramDay: ((slotToken: string) => void) | null;
  readonly onAddDay: ((weekday: number) => void) | null;
  readonly returnFocusToken: string | null;
  readonly addDayLabel: string;
  /** Why no training may be added to this phase, or `null`. Forwarded to each column unchanged. */
  readonly addDayRefusal: AddDayRefusalView | null;
  /** The session move, forwarded unchanged. `null` on a plan that cannot be edited. */
  readonly move: SessionMoveProps | null;
  /** Duplicate and delete, forwarded to every card. `null` on a plan that cannot be edited. */
  readonly actions: DayCardActions | null;
  /** The keyboard instruction every grip's `aria-describedby` points at. */
  readonly moveHint: string;
  /** Read into the canvas's live region — every move, preview and refusal. */
  readonly announcement: string;
  /**
   * Which STOP a keyboard lift is pointed at, so that column lights up — 0-based, so weekday 1 is
   * `0`. `null` when nothing is lifted.
   */
  readonly pendingTarget: number | null;
  /**
   * The phase the canvas is showing — the RESOLVED index, not the raw `?phase=`.
   *
   * Its only job here is to replay the handoff's `swapA` / `swapB` fade when the trainer steps
   * between phases. The columns themselves come from `columns`; this is the signal that they came
   * from somewhere else.
   */
  readonly phaseIndex: number;
  /**
   * What is open OVER this canvas's right-hand end: the day panel, the Buddy panel, both, or nothing.
   *
   * The one thing the pane does with it is grow trailing scroll room that wide, out of flow, so a
   * column standing under them can be scrolled clear of them. See
   * `.week[data-panel-room]` — the pane still does not learn what a panel IS, only that its right
   * hand end is covered by something that wide.
   */
  readonly panelRoom: OverlayRoom;
  /**
   * The slot token of the day whose panel is open, or `null`.
   *
   * The mock's `active`, which paints that column's card box in the accent with a 3px ember ring. A
   * TOKEN rather than a weekday index, so a day moved while its panel is open keeps its ring.
   */
  readonly openDaySlotToken: string | null;
}

export function WeekPane({
  id,
  columns,
  labelledBy,
  onRenameSession,
  onProgramDay,
  onAddDay,
  returnFocusToken,
  addDayLabel,
  addDayRefusal,
  move,
  actions,
  moveHint,
  announcement,
  pendingTarget,
  phaseIndex,
  panelRoom,
  openDaySlotToken,
}: WeekPaneProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const headingId = `${id}-heading`;

  /**
   * The parity that replays the fade when the trainer steps to another phase, and the `null` that
   * keeps a cold entry still. Both rules, and the reason there are two keyframes for one animation,
   * live in `useSwapReplay` — the day panel's own day-step fade is the second surface asking the
   * same question, and asking it once is what stops the two drifting apart.
   */
  const replay = useSwapReplay(phaseIndex, useScreenMotionPreference());
  const swapClass = replay === null ? '' : String(replay === 'a' ? swap.swapA : swap.swapB);

  // The pane is the scroller, so it is what a drag near an edge has to move. BOTH axes: it clips a
  // heavy week vertically as it always did, and it clips horizontally while the day panel is open,
  // where the trailing room puts a covered column out of reach of a stationary pointer.
  useAutoScroll(ref, 'all');

  return (
    <div
      ref={ref}
      className={styles.pane}
      id={id}
      /* This box is the horizontal scrollport, and `useRevealOpenDay` has to find it from
       * `WeekCanvas` — where the ref above is not available and where a second ref prop would
       * have to be merged with the two hooks already holding this one. An attribute is the seam
       * the calendar's overlay measurement already uses. CANVAS-neutral rather than week-specific:
       * the sequence pane carries the same attribute and the same hook measures it. */
      data-canvas-scroller=""
      role="tabpanel"
      aria-labelledby={labelledBy}
      tabIndex={0}
    >
      <h2 id={headingId} className={visuallyHidden.srOnly}>
        {t('web:planBuilder.week')}
      </h2>
      {/* Always mounted, empty when there is nothing to say — a region inserted together with its
        * text is not reliably announced. It sits inside the pane rather than beside it so it moves
        * with the canvas it describes. */}
      <LiveRegionAnnouncer message={announcement} />
      {/* The keyboard instruction, once. Every grip on the week points its `aria-describedby`
        * here, so the sentence exists in one place and is announced after the grip's own name. */}
      {move !== null && (
        <p id={MOVE_HINT_ID} className={visuallyHidden.srOnly}>
          {moveHint}
        </p>
      )}
      {/* The rename affordance's DESCRIPTION, once for the whole week — see `RENAME_HINT_ID`. It
        * carries the verb that used to be an `aria-label` on every card's heading, which made the
        * document outline read as seven commands instead of seven workouts. Rendered only when the
        * plan is editable, because a description of an action nobody can take is noise. */}
      {onRenameSession !== null && (
        <p id={RENAME_HINT_ID} className={visuallyHidden.srOnly}>
          {t('web:planBuilder.renameHint')}
        </p>
      )}
      {/* Labelled BY the heading rather than carrying its own copy of the same words: one string,
        * in one place, and a rename cannot leave the two disagreeing. */}
      <div
        className={`${String(styles.week)} ${swapClass}`}
        role="list"
        aria-labelledby={headingId}
        data-panel-room={panelRoom ?? undefined}
      >
        {columns.map((column) => (
          <DayColumn
            key={column.key}
            column={column}
            onRenameSession={onRenameSession}
            onProgramDay={onProgramDay}
            onAddDay={column.day === null ? onAddDay : null}
            returnFocusToken={returnFocusToken}
            addDayLabel={addDayLabel}
            addDayTileLabel={t('web:planBuilder.addDayTile')}
            addDayRefusal={addDayRefusal}
            move={move}
            actions={actions}
            isPendingTarget={pendingTarget === column.weekday - 1}
            isSelected={openDaySlotToken !== null && column.day?.slotToken === openDaySlotToken}
          />
        ))}
      </div>
    </div>
  );
}
